import { useCallback, useEffect, useMemo, useState } from "react";

import { usePersistedFilters } from "src/hooks/usePersistedFilters";

export type BaseTableQueryParams = {
  offset: number;
  limit: number;
  sortBy: string;
  searchText: string;
};

export type SortModelItem = {
  field: string;
  sort?: "asc" | "desc" | null;
};

export type PaginationModel = {
  page: number;
  pageSize: number;
};

const SORT_BY_PATTERN = /^[A-Za-z0-9_.]+:(ASC|DESC)$/i;

const isValidSortBy = (value: any): value is string =>
  typeof value === "string" && SORT_BY_PATTERN.test(value);

const sanitizeText = (value: any): string =>
  typeof value === "string" && value !== "undefined" && value !== "null"
    ? value
    : "";

type UseTableQueryConfig<TExtra> = {
  pageKey: string;
  pageSize?: number;
  defaultSortBy?: string;
  /** Fixed params and filters merged into every request. */
  extraParams?: TExtra;
  defaultFilterKey?: Record<string, any>;
  omitFromPersist?: string[];
};

/**
 * Query params + pagination + persisted filters for a server-paginated page.
 *
 * @example
 * const { queryParams, paginationModel, filterKey, handleSearch, handleSort } =
 *   useTableQuery({
 *     pageKey: "progressDetails",
 *     extraParams: { landOwnershipStatus: "APPROVED", stateIds: undefined },
 *     defaultFilterKey: { stateIds: null },
 *   });
 */
export function useTableQuery<TExtra extends Record<string, any> = {}>({
  pageKey,
  pageSize = 20,
  defaultSortBy = "updated_at:DESC",
  extraParams,
  defaultFilterKey = {},
  omitFromPersist = [],
}: UseTableQueryConfig<TExtra>) {
  const { persisted, saveFilters, clearFilters } = usePersistedFilters(pageKey);

  const [queryParams, setQueryParams] = useState<
    BaseTableQueryParams & TExtra & Record<string, any>
  >(() => {
    const defaults = {
      offset: 0,
      limit: pageSize,
      sortBy: defaultSortBy,
      searchText: "",
      ...(extraParams ?? ({} as TExtra)),
    };

    // Restore only declared keys — a stored blob can outlive a deploy.
    const stored = persisted?.queryParams ?? {};
    const restored = Object.keys(defaults).reduce((acc, key) => {
      if (stored[key] !== undefined) {
        acc[key] = stored[key];
      }
      return acc;
    }, {} as Record<string, any>);

    const merged = { ...defaults, ...restored };

    const limit = Number(merged.limit) > 0 ? Number(merged.limit) : pageSize;
    const offset = Number(merged.offset) >= 0 ? Number(merged.offset) : 0;

    return {
      ...merged,
      limit,
      offset,
      sortBy: isValidSortBy(merged.sortBy) ? merged.sortBy : defaultSortBy,
      searchText: sanitizeText(merged.searchText),
    };
  });

  const [filterKey, setFilterKey] = useState<Record<string, any>>(
    () => persisted?.filterKey ?? defaultFilterKey
  );

  const paginationModel: PaginationModel = useMemo(() => {
    const limit = queryParams.limit || pageSize;
    return {
      page: Math.floor((queryParams.offset || 0) / limit),
      pageSize: limit,
    };
  }, [queryParams.offset, queryParams.limit, pageSize]);

  useEffect(() => {
    const persistable = { ...queryParams };
    ["download", ...omitFromPersist].forEach((key) => delete persistable[key]);
    saveFilters({ queryParams: persistable, filterKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, filterKey]);

  const applyAndResetPage = useCallback((params: Record<string, any>) => {
    setQueryParams((prev) => ({ ...prev, ...params, offset: 0 }));
  }, []);

  const handleSearch = useCallback((text: string) => {
    const searchText = `${text ?? ""}`;
    // SearchBar also emits on mount with its restored value; ignore no-ops.
    setQueryParams((prev) =>
      prev.searchText === searchText ? prev : { ...prev, searchText, offset: 0 }
    );
  }, []);

  /** Params go to the request; `filterKeyPatch` overrides what the control shows. */
  const handleFilterChange = useCallback(
    (params: Record<string, any>, filterKeyPatch?: Record<string, any>) => {
      setFilterKey((prev) => ({ ...prev, ...(filterKeyPatch ?? params) }));
      applyAndResetPage(params);
    },
    [applyAndResetPage]
  );

  const handleSort = useCallback(
    (sortModel: SortModelItem[]) => {
      const { field, sort } = sortModel?.[0] ?? {};
      applyAndResetPage({
        sortBy:
          field && sort ? `${field}:${sort.toUpperCase()}` : defaultSortBy,
      });
    },
    [applyAndResetPage, defaultSortBy]
  );

  const handlePaginationChange = useCallback((model: PaginationModel) => {
    setQueryParams((prev) => ({
      ...prev,
      limit: model.pageSize,
      offset: model.page * model.pageSize,
    }));
  }, []);

  /** Pulls an out-of-range page back to the last valid one. Returns true if it moved. */
  const clampToTotal = useCallback(
    (total: number): boolean => {
      if (!Number.isFinite(total) || total < 0) {
        return false;
      }

      const limit = queryParams.limit || 1;
      const lastOffset =
        total === 0 ? 0 : Math.floor((total - 1) / limit) * limit;

      if (queryParams.offset <= lastOffset) {
        return false;
      }

      setQueryParams((prev) => ({ ...prev, offset: lastOffset }));
      return true;
    },
    [queryParams.limit, queryParams.offset]
  );

  const resetFilters = useCallback(() => {
    setFilterKey(defaultFilterKey);
    setQueryParams({
      offset: 0,
      limit: pageSize,
      sortBy: defaultSortBy,
      searchText: "",
      ...(extraParams ?? ({} as TExtra)),
    });
    clearFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearFilters, defaultSortBy, pageSize]);

  return {
    queryParams,
    setQueryParams,
    paginationModel,
    filterKey,
    setFilterKey,
    handleSearch,
    handleFilterChange,
    handleSort,
    handlePaginationChange,
    clampToTotal,
    resetFilters,
    searchDefaultValue: sanitizeText(persisted?.queryParams?.searchText),
  };
}

export default useTableQuery;
